import React from 'react';

import clsxm from '@/lib/clsxm';
import useBreakpoints, {
  Breakpoint,
  BreakpointMap,
} from '@/hooks/use-breakpoints';

import { CarouselItem, CarouselVariant } from '@/features/carousel';

export enum InfiniteCarouselDirection {
  LEFT_TO_RIGHT = 'left-to-right',
  RIGHT_TO_LEFT = 'right-to-left',
}

type InfiniteCarouselSpeed = 'slow' | 'medium' | 'fast' | 'very-slow';

export type InfiniteCarouselProps = {
  variant: CarouselVariant.INFINITE;
  direction: InfiniteCarouselDirection;
  items: CarouselItem[];
  speed?: InfiniteCarouselSpeed;
  heightClassName?: string;
  pauseOnHover?: boolean;
};

const speedMap: Record<InfiniteCarouselSpeed, Partial<BreakpointMap>> = {
  'very-slow': {
    [Breakpoint.sm]: 15,
    [Breakpoint.lg]: 30,
  },

  slow: {
    [Breakpoint.sm]: 10,
    [Breakpoint.lg]: 20,
  },
  medium: {
    [Breakpoint.sm]: 5,
    [Breakpoint.lg]: 10,
  },
  fast: {
    [Breakpoint.lg]: 1,
  },
};

const TIMES_TO_REPEAT = 3;

export default function InfiniteCarousel({
  direction,
  speed = 'medium',
  heightClassName,
  items,
  pauseOnHover,
}: InfiniteCarouselProps) {
  const { currentEntry: animationDuration } = useBreakpoints(speedMap[speed]);
  const isLeftToRight = direction === InfiniteCarouselDirection.LEFT_TO_RIGHT;

  return (
    <div
      className={clsxm(
        'relative flex w-[200vw] flex-nowrap gap-4 overflow-hidden p-1',
        heightClassName
      )}
      style={
        {
          '--infinite-carousel-speed': `${animationDuration * items.length}s`,
        } as React.CSSProperties
      }
    >
      <div
        className={clsxm(
          'animate absolute left-0 flex w-[200vw] items-center gap-4',
          heightClassName,
          {
            'animate-infiniteCarouselLeftToRight': isLeftToRight,
            'animate-infiniteCarouselRightToLeft': !isLeftToRight,
            'hover:animate-pause': pauseOnHover,
          }
        )}
      >
        {Array.from({ length: TIMES_TO_REPEAT }).map(() =>
          items.map((item) => (
            <React.Fragment key={item.id}>{item.component}</React.Fragment>
          ))
        )}
      </div>
    </div>
  );
}
